import { exportDatabaseBackup } from './exportUtils';
import { updatesBlocked } from './updateGate';
import {
  showBackupStarting,
  showBackupFinished,
  showBackupFailed,
} from './updateNotice';

/**
 * Getting a new build onto a phone that is already running an old one.
 *
 * This lived inline in index.tsx, where nothing could test it. It is the part
 * of the app that decides when new code is allowed to touch the database, so
 * it is worth being able to state its behaviour in tests.
 *
 * The order this enforces, and why:
 *
 *   1. a new worker installs and WAITS — it does not take over on its own
 *   2. once no form is open, the whole database is exported to a file
 *   3. only then is the worker told to take over (SKIP_WAITING)
 *   4. it takes control, and the page reloads into the new build
 *
 * Step 1 is the load-bearing one. The worker used to call `skipWaiting()` as
 * soon as it installed, which made steps 2 and 3 a race rather than a
 * sequence: measured on 2026-08-08, the reload was already under way ~20ms
 * before the backup began, and the backup only completed because the reload's
 * network round-trip happened to take about a second. It also meant the new
 * worker activated — purging every older cache — while the old page was still
 * on screen, leaving that page running code whose assets had just been
 * deleted.
 */

/**
 * Constant across builds, deliberately.
 *
 * This used to be `service-worker.js?v=<build id>`. A page always registers
 * its own build's id, so the moment an update landed and the page reloaded, it
 * registered a *different* script URL — which the browser treats as a brand
 * new registration and installs from scratch. Measured: every deploy produced
 * two installs, two pre-update backups (the second one pointlessly labelled
 * with the build that had just arrived) and two reloads. The build id already
 * reaches the worker where it matters — it is stamped into CACHE_NAME by
 * scripts/stamp-build-id.js, so the file's bytes change on every deploy and
 * the browser notices without any help from the URL.
 */
export const SERVICE_WORKER_PATH = '/service-worker.js';

/** How often to re-check whether an open form has closed. */
const DEFAULT_RECHECK_MS = 2000;

export interface ServiceWorkerUpdateOptions {
  container?: ServiceWorkerContainer | null;
  reload?: () => void;
  scriptUrl?: string;
  buildId?: string;
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  recheckMs?: number;
}

export function registerServiceWorkerUpdates(
  options: ServiceWorkerUpdateOptions = {}
): Promise<ServiceWorkerRegistration | null> {
  const {
    container = typeof navigator !== 'undefined' ? navigator.serviceWorker : null,
    reload = () => window.location.reload(),
    scriptUrl = `${process.env.PUBLIC_URL || ''}${SERVICE_WORKER_PATH}`,
    buildId = process.env.REACT_APP_BUILD_ID || 'dev',
    storage = typeof localStorage !== 'undefined' ? localStorage : undefined,
    recheckMs = DEFAULT_RECHECK_MS,
  } = options;

  if (!container) return Promise.resolve(null);

  return container
    // `updateViaCache: 'none'` keeps the HTTP cache out of the update check.
    // Cloudflare Pages serves this file with `max-age=14400` by default, which
    // public/_headers now overrides — this is the same instruction stated on
    // the browser side, so a stale edge or proxy cannot hide a new build.
    .register(scriptUrl, { updateViaCache: 'none' })
    .then(registration => {
      let hasReloaded = false;
      let recheckTimer: ReturnType<typeof setTimeout> | undefined;
      // Per WORKER, not a single latch. The browser can replace the worker
      // that is waiting — it does exactly that on the one deploy where the
      // registration URL changes — and a one-shot latch would leave the
      // replacement with no way to be told to take over, stranding the device
      // until the next reload.
      const invited = new WeakSet<ServiceWorker>();

      // Wrapped rather than chained directly: an update check that throws
      // synchronously must not take the rest of the wiring — the handover
      // listeners below — down with it. The re-check afterwards catches a
      // worker that reached `waiting` without us seeing an `updatefound`.
      const requestUpdate = () => {
        try {
          Promise.resolve(registration.update())
            .then(() => consider(registration.waiting))
            .catch(error => {
              console.error('Service Worker update check failed:', error);
            });
        } catch (error) {
          console.error('Service Worker update check failed:', error);
        }
      };

      const backupKey = `timetrakr-preupdate-backup-${buildId}`;

      /**
       * Export the whole database to a file, at most once per page.
       *
       * Keyed on the BUILD id rather than package.json's `version`, which is
       * hand-typed and in practice never moves — it sat at 1.7.4 across the
       * entire sync-hardening batch. Keyed on `version`, the second deploy
       * onwards would find the flag already 'done' and skip the backup
       * entirely: precisely the deploy where a schema change could eat data.
       *
       * Never rejects. Whether the file was written is reported on screen, but
       * it must not decide whether the update proceeds — see below.
       */
      let backup: Promise<void> | null = null;
      const takeBackupOnce = (): Promise<void> => {
        if (backup) return backup;
        backup = (async () => {
          if (storage?.getItem(backupKey) === 'done') return;
          // Say what is happening BEFORE the file appears. An unexplained
          // download is the thing an employee dismisses or blocks, and this is
          // the only copy of their data taken before new code can touch it.
          showBackupStarting();
          try {
            await exportDatabaseBackup();
            storage?.setItem(backupKey, 'done');
            showBackupFinished();
          } catch (error) {
            console.error('Pre-update backup failed:', error);
            showBackupFailed();
          }
        })();
        return backup;
      };

      const backupThenHandOver = async (worker: ServiceWorker) => {
        if (invited.has(worker)) return;
        invited.add(worker);
        // Shared promise, so a second worker arriving mid-export waits for the
        // same backup rather than racing it or triggering another one. It
        // never rejects — a backup that could not be written is worth saying
        // out loud, but refusing to update because of it would strand the
        // device on an old build, which is the more dangerous outcome and the
        // one this whole exercise exists to prevent.
        await takeBackupOnce();
        worker.postMessage({ type: 'SKIP_WAITING' });
      };

      /**
       * Hand over as soon as nothing is being typed.
       *
       * Re-checks on a timer as well as on the events, because the old code
       * re-armed on `focus` alone: an installed PWA returning from the
       * background fires `visibilitychange`, and `focus` is not guaranteed, so
       * a deferred update could sit there indefinitely. Nothing here ever
       * overrides the gate — a form that stays open keeps the OLD build
       * running, with its cache intact, which is the safe place to wait.
       */
      const handOverWhenIdle = (worker: ServiceWorker) => {
        if (invited.has(worker)) return;
        if (updatesBlocked()) {
          clearTimeout(recheckTimer);
          recheckTimer = setTimeout(() => handOverWhenIdle(worker), recheckMs);
          return;
        }
        clearTimeout(recheckTimer);
        void backupThenHandOver(worker);
      };

      /**
       * Take an interest in a worker wherever we find one.
       *
       * Deliberately not driven by `updatefound` alone. Navigating to a
       * controlled page makes the browser check for a new worker by itself,
       * before any of this code has run, so that event has often already
       * fired by the time we could listen for it. Observed in the browser on
       * 2026-08-08: a deploy installed, parked itself in `waiting`, and needed
       * a SECOND reopen of the app before it took over — which from the
       * outside looks exactly like "the update didn't arrive".
       */
      const consider = (worker: ServiceWorker | null) => {
        if (!worker || invited.has(worker) || !container.controller) return;
        if (worker.state === 'installed') {
          handOverWhenIdle(worker);
          return;
        }
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && container.controller) {
            handOverWhenIdle(worker);
          }
        });
      };

      registration.addEventListener('updatefound', () => consider(registration.installing));

      // Whatever state this registration is already in when the page starts:
      // a worker left waiting by a previous session, or one the browser began
      // installing during this very navigation.
      consider(registration.waiting);
      consider(registration.installing);

      container.addEventListener('controllerchange', () => {
        if (hasReloaded) return;
        hasReloaded = true;
        reload();
      });

      requestUpdate();
      window.addEventListener('focus', requestUpdate);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) requestUpdate();
      });

      return registration;
    })
    .catch(error => {
      console.error('Service Worker registration failed:', error);
      return null;
    });
}
